import { Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';
import { toast } from '@nucleus-commerce/ui/components/sonner';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useInboxLive } from '../hooks/use-inbox-live';
import { desktopNotificationsSupported, playChime, requestDesktopPermission } from '../lib/alerts';
import { useInboxPrefs, type AgentStatus } from '../lib/inbox-prefs';

const STATUS_DOT: Record<AgentStatus, string> = {
  online: 'bg-success',
  away: 'bg-warning',
  invisible: 'bg-muted-foreground/40',
};

/** Agent status (drives presence while the inbox is open), alerts, and the connection state. */
export function InboxToolbar() {
  const { t } = useTranslation('conversations');
  const { can } = usePermissions();
  const live = useInboxLive();
  const { status, sound, desktop, setStatus, setSound, setDesktop } = useInboxPrefs();
  const agent = can('conversations.write');

  const toggleDesktop = async () => {
    if (desktop) {
      setDesktop(false);
      return;
    }
    if (await requestDesktopPermission()) setDesktop(true);
    else toast.error(t('toolbar.desktopBlocked'));
  };

  const toggleSound = () => {
    const next = !sound;
    setSound(next);
    // The click is the gesture browsers want before audio; it also previews the chime.
    if (next) playChime();
  };

  const connectionLabel =
    status === 'invisible'
      ? t('toolbar.connection.invisible')
      : live.connection === 'socket'
        ? t('toolbar.connection.live')
        : live.connection === 'connecting'
          ? t('toolbar.connection.connecting')
          : t('toolbar.connection.polling');

  return (
    <div className="flex items-center gap-1">
      <span className="text-muted-foreground hidden text-xs md:inline" role="status">
        {connectionLabel}
      </span>
      {agent ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 gap-2">
              <span className={cn('size-2 rounded-full', STATUS_DOT[status])} aria-hidden />
              {t(`toolbar.status.${status}`)}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>{t('toolbar.statusLabel')}</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={status}
              onValueChange={(value) => setStatus(value as AgentStatus)}
            >
              {(['online', 'away', 'invisible'] as const).map((value) => (
                <DropdownMenuRadioItem
                  key={value}
                  value={value}
                  className="flex-col items-start gap-0"
                >
                  <span>{t(`toolbar.status.${value}`)}</span>
                  <span className="text-muted-foreground text-xs">
                    {t(`toolbar.statusHint.${value}`)}
                  </span>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      <Button
        variant="ghost"
        size="icon"
        className="size-8"
        aria-pressed={sound}
        aria-label={sound ? t('toolbar.soundOff') : t('toolbar.soundOn')}
        title={sound ? t('toolbar.soundOff') : t('toolbar.soundOn')}
        onClick={toggleSound}
      >
        {sound ? <Volume2 aria-hidden /> : <VolumeX aria-hidden />}
      </Button>
      {desktopNotificationsSupported() ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-pressed={desktop}
          aria-label={desktop ? t('toolbar.desktopOff') : t('toolbar.desktopOn')}
          title={desktop ? t('toolbar.desktopOff') : t('toolbar.desktopOn')}
          onClick={() => void toggleDesktop()}
        >
          {desktop ? <Bell aria-hidden /> : <BellOff aria-hidden />}
        </Button>
      ) : null}
    </div>
  );
}
